import React from 'react';
import qs from 'qs';
import Icon from '@mdi/react';
import { mdiChat } from '@mdi/js';
import { TabletButton } from '@campfire/tablet-button';
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton as MuiIconButton,
  Theme,
  Popover,
} from '@material-ui/core';
import { DataGrid, GridCellParams, GridColumns } from '@material-ui/data-grid';
import { GetApp, ExpandMore, Email, Fullscreen, Close, ChatBubble } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useCampfireFetch } from '../../../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../../../global/config/useApiUrl';
import { VolunteerProfile } from '../../../../../../../../common/AvatarName';
import { WidgetContainer, WidgetContent } from '../../WidgetContainer';
import { WidgetHeader } from '../../WidgetHeader';
import { VolunteerSubmitValues } from '../../../../../../../admin/tasks/VolunteerSubmit/VolunteerSubmitValues';
import { VolunteerTaskViewDialog } from '../../../../../../../program-manager/volunteer-common-profile/profile-sections/tasks-section/VolunteerCompletedTaskItem';
import { VolunteerTaskAddDialog } from '../../../../../../../program-manager/volunteer-common-profile/profile-sections/tasks-section/VolunteerTaskAddDialog';
import { TooltipMenu } from '../../../../../../../../common/tooltip-menu';
import { useCampfireTheme } from '../../../../../../../../theme/useCampfireTheme';
import {
  downloadCSV,
  escapeDangerousCSVCharacters,
  replaceDoubleQuoteInString,
} from '../../../../../../../../common/functions/csv-utils';
import { VolunteerCommonProfileEmailDialog } from '../../../../../../../program-manager/volunteer-common-profile/profile-sections/action-menu/VolunteerCommonProfileEmailDialog';
import { VolunteerCommonProfileSmsDialog } from '../../../../../../../program-manager/volunteer-common-profile/profile-sections/action-menu/VolunteerCommonProfileSmsDialog';
import { useOrgInfo } from '../../../../../../../../global/auth/use-org-info/use-org-info';

type TaskTableData = {
  completedTaskId?: string;
  taskId: string;
  profileId: string;
  userId: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  avatarUrl: string;
  email: string;
  expiryDate?: string;
  taskTitle?: string;
  dateSubmitted?: string;
  hasExpired?: boolean;
  hasPreviousVersion?: boolean;
};

type TaskOption = {
  taskId: string;
  taskTitle: string;
  cakeTitle: string;
};

type TaskTableResponse = {
  data: TaskTableData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  availableTasks: TaskOption[];
};

type TasksRestTableWidgetProps = {
  filters: string;
  range?: string | null;
  target: 'awaiting' | 'completed' | 'incomplete' | 'expiring';
  showExpiryDate?: boolean;
  title?: string;
  subTitle?: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contactButton: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontSize: '14px',
      fontWeight: 500,
      '& span': {
        fontSize: '14px',
      },
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
      padding: '6px 12px',
    },
  })
);

const CustomNoRowsOverlay = () => {
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        fontSize: '14px',
        color: '#666',
        gap: '8px',
      }}
    >
      <span role='img' aria-label='celebration'>
        🎉
      </span>
      Nothing to see!
    </Box>
  );
};

export const TasksRestTableWidget: React.FC<TasksRestTableWidgetProps> = ({
  filters,
  range,
  target,
  showExpiryDate = false,
  title,
  subTitle,
}) => {
  const apiUrl = useApiUrl();
  const { theme } = useCampfireTheme();
  const orgInfo = useOrgInfo();
  const classes = useStyles();
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [selectedCompletedTaskId, setSelectedCompletedTaskId] = React.useState<string | null>(null);
  const [selectedCompletedTaskData, setSelectedCompletedTaskData] = React.useState<{
    completedTaskId: string;
    taskId: string;
    userId: string;
    profileId: string;
  } | null>(null);
  const [selectedIncompleteTaskData, setSelectedIncompleteTaskData] = React.useState<{
    taskId: string;
    userId: string;
    profileId: string;
  } | null>(null);
  const [openTasksDropdown, setOpenTasksDropdown] = React.useState(false);
  const [downloadingCsv, setDownloadingCsv] = React.useState(false);
  const [availableTasks, setAvailableTasks] = React.useState<TaskOption[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = React.useState<string[]>([]);
  const [expandedView, setExpandedView] = React.useState(false);
  const [contactMenuAnchor, setContactMenuAnchor] = React.useState<{ [key: string]: HTMLElement | null }>({});
  const [emailDialogOpen, setEmailDialogOpen] = React.useState<{ [key: string]: boolean }>({});
  const [smsDialogOpen, setSmsDialogOpen] = React.useState<{ [key: string]: boolean }>({});

  const { programIds, activityIds, roleIds } = JSON.parse(filters) as {
    programIds?: string[];
    activityIds?: string[];
    roleIds?: string[];
  };

  const campfireQuery = useCampfireFetch<TaskTableResponse>({ defer: true });
  const [data, setData] = React.useState<TaskTableData[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    setLoading(true);
    campfireQuery
      .run({
        url: `${apiUrl}/vm/dashboard/management/tasks/${target}/table`,
        params: {
          filters: {
            programIds: programIds || [],
            activityIds: activityIds || [],
            roleIds: roleIds || [],
            taskIds: selectedTaskIds.length > 0 ? selectedTaskIds : undefined,
          },
          range: range || undefined,
          page: page + 1, // DataGrid uses 0-based, API uses 1-based
          limit: pageSize,
        },
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'brackets', encode: false });
        },
      })
      .then((response) => {
        if (response?.data?.data) {
          const mappedData = response.data.data.data.map((item: any) => ({
            completedTaskId: item.completed_task_id,
            taskId: item.task_id,
            profileId: item.profile_id,
            userId: item.user_id,
            firstName: item.first_name,
            lastName: item.last_name,
            preferredName: item.preferred_name,
            avatarUrl: item.avatar_object_url,
            email: item.email,
            expiryDate: item.expiry_date,
            taskTitle: item.task_title,
            dateSubmitted: item.date_submitted,
            hasExpired: item.has_expired === 1 || item.has_expired === '1' || item.has_expired === true,
            hasPreviousVersion:
              item.has_previous_version === 1 ||
              item.has_previous_version === '1' ||
              item.has_previous_version === true,
          }));

          // Debug logging after mapping
          if (target === 'incomplete' && mappedData.length > 0) {
            console.log(
              'After mapping - hasExpired:',
              mappedData[0].hasExpired,
              'hasPreviousVersion:',
              mappedData[0].hasPreviousVersion
            );
          }

          setData(mappedData);
          setTotal(response.data.data.total);

          // Set available tasks (only on first load to avoid resetting on page changes)
          if (response.data.data.availableTasks && page === 0) {
            setAvailableTasks(
              response.data.data.availableTasks.map((task: any) => ({
                taskId: task.task_id,
                taskTitle: task.task_title,
                cakeTitle: task.cake_title,
              }))
            );
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, pageSize, filters, range, target, refreshKey, selectedTaskIds]);

  const rows = data.map((item, index) => ({
    id: item.completedTaskId || `${item.taskId}-${item.profileId}-${index}`,
    taskId: item.taskId,
    profileId: item.profileId,
    userId: item.userId,
    firstName: item.firstName,
    lastName: item.lastName,
    preferredName: item.preferredName,
    avatarUrl: item.avatarUrl,
    expiryDate: item.expiryDate,
    taskTitle: item.taskTitle,
    dateSubmitted: item.dateSubmitted,
    completedTaskId: item.completedTaskId,
    hasExpired: item.hasExpired,
    hasPreviousVersion: item.hasPreviousVersion,
  }));

  const columns: GridColumns = [
    {
      field: 'volunteerName',
      headerName: 'Volunteer',
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <VolunteerProfile
            inTable
            profileId={row.profileId}
            userId={row.userId}
            lastName={row.lastName}
            preferredName={row.preferredName}
            avatarUrl={row.avatarUrl}
            name={`${row.preferredName} ${row.lastName}`}
          />
        );
      },
    },
  ];

  // Add task-specific columns based on target
  if (target === 'awaiting') {
    columns.push({
      field: 'taskTitle',
      headerName: 'Task',
      flex: 1,
      minWidth: 120,
    });
    columns.push({
      field: 'dateSubmitted',
      headerName: 'Date Submitted',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value as string);
        return date.toLocaleDateString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    });
    if (showExpiryDate) {
      columns.push({
        field: 'expiryDate',
        headerName: 'Expiry Date',
        width: 150,
        valueFormatter: (params) => {
          if (!params.value) return '';
          const date = new Date(params.value as string);
          return date.toLocaleDateString('en-AU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        },
      });
    }
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const rowId = `${params.row.taskId}-${params.row.profileId}`;

        return (
          <Box display='flex' style={{ gap: '8px' }}>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={(e) => {
                setContactMenuAnchor((prev) => ({ ...prev, [rowId]: e.currentTarget }));
              }}
              style={{
                textTransform: 'none',
                boxShadow: 'none',
                minWidth: 'auto',
                padding: '4px 8px',
              }}
            >
              <Icon path={mdiChat} style={{ height: '16px', width: '16px' }} />
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={() => {
                setSelectedCompletedTaskId(params.row.completedTaskId);
              }}
              style={{
                textTransform: 'none',
                boxShadow: 'none',
                padding: '4px 8px',
                minWidth: 'auto',
              }}
            >
              Review
            </Button>
          </Box>
        );
      },
    });
  } else if (target === 'expiring') {
    columns.push({
      field: 'taskTitle',
      headerName: 'Task',
      flex: 1,
      minWidth: 120,
    });
    columns.push({
      field: 'expiryDate',
      headerName: 'Expiry Date',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value as string);
        return date.toLocaleDateString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    });
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const rowId = `${params.row.taskId}-${params.row.profileId}`;

        return (
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={(e) => {
              setContactMenuAnchor((prev) => ({ ...prev, [rowId]: e.currentTarget }));
            }}
            style={{
              textTransform: 'none',
              boxShadow: 'none',
              minWidth: 'auto',
              padding: '4px 8px',
            }}
          >
            <Icon path={mdiChat} style={{ height: '16px', width: '16px' }} />
          </Button>
        );
      },
    });
  } else if (target === 'completed') {
    columns.push({
      field: 'taskTitle',
      headerName: 'Task',
      flex: 1,
      minWidth: 120,
    });
    columns.push({
      field: 'dateSubmitted',
      headerName: 'Date Submitted',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value as string);
        return date.toLocaleDateString('en-AU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    });
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const rowId = `${params.row.taskId}-${params.row.profileId}`;

        return (
          <Box display='flex' style={{ gap: '8px' }}>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={(e) => {
                setContactMenuAnchor((prev) => ({ ...prev, [rowId]: e.currentTarget }));
              }}
              style={{
                textTransform: 'none',
                boxShadow: 'none',
                minWidth: 'auto',
                padding: '4px 8px',
              }}
            >
              <Icon path={mdiChat} style={{ height: '16px', width: '16px' }} />
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={() => {
                setSelectedCompletedTaskData({
                  completedTaskId: params.row.completedTaskId,
                  taskId: params.row.taskId,
                  userId: params.row.userId,
                  profileId: params.row.profileId,
                });
              }}
              style={{
                textTransform: 'none',
                boxShadow: 'none',
                padding: '4px 8px',
                minWidth: 'auto',
              }}
            >
              Review
            </Button>
          </Box>
        );
      },
    });
  } else if (target === 'incomplete') {
    columns.push({
      field: 'taskTitle',
      headerName: 'Task',
      flex: 1,
      minWidth: 120,
    });
    columns.push({
      field: 'hasExpired',
      headerName: 'Expired',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => {
        return <Checkbox checked={(params.value as boolean) || false} disabled size='small' />;
      },
    });
    columns.push({
      field: 'hasPreviousVersion',
      headerName: 'Previous Version',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => {
        return <Checkbox checked={(params.value as boolean) || false} disabled size='small' />;
      },
    });
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const rowId = `${params.row.taskId}-${params.row.profileId}`;

        return (
          <Box display='flex' style={{ gap: '8px' }}>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={(e) => {
                setContactMenuAnchor((prev) => ({ ...prev, [rowId]: e.currentTarget }));
              }}
              style={{
                textTransform: 'none',
                boxShadow: 'none',
                minWidth: 'auto',
                padding: '4px 8px',
              }}
            >
              <Icon path={mdiChat} style={{ height: '16px', width: '16px' }} />
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={() => {
                setSelectedIncompleteTaskData({
                  taskId: params.row.taskId,
                  userId: params.row.userId,
                  profileId: params.row.profileId,
                });
              }}
              style={{
                textTransform: 'none',
                boxShadow: 'none',
                padding: '4px 8px',
                minWidth: 'auto',
              }}
            >
              Review
            </Button>
          </Box>
        );
      },
    });
  } else {
    // Default: show Task ID
    columns.push({
      field: 'taskId',
      headerName: 'Task ID',
      width: 120,
    });
  }

  const getDefaultTitle = () => {
    switch (target) {
      case 'awaiting':
        return 'Pending Tasks';
      case 'completed':
        return 'Tasks Recently Completed';
      case 'incomplete':
        return 'Tasks Incomplete';
      case 'expiring':
        return 'Expiring Tasks';
      default:
        return 'Tasks';
    }
  };

  const getDefaultSubTitle = () => {
    switch (target) {
      case 'awaiting':
        return 'Tasks requiring attention';
      case 'completed':
        return 'Recently completed';
      case 'incomplete':
        return 'Outstanding tasks';
      case 'expiring':
        return 'Next 30 days';
      default:
        return '';
    }
  };

  const handleTaskSelection = (taskId: string, checked: boolean) => {
    if (checked) {
      setSelectedTaskIds((prev) => [...prev, taskId]);
    } else {
      setSelectedTaskIds((prev) => prev.filter((id) => id !== taskId));
    }
  };

  const getTaskDropdownText = () => {
    if (selectedTaskIds.length === 0) {
      return 'All Tasks';
    }
    if (selectedTaskIds.length === 1) {
      const selectedTask = availableTasks.find((task) => task.taskId === selectedTaskIds[0]);
      return selectedTask ? selectedTask.taskTitle : '1 Task';
    }
    return `${selectedTaskIds.length} Tasks`;
  };

  const handleExportCsv = async () => {
    setDownloadingCsv(true);

    try {
      // First, fetch the first page to get total count
      const firstPageResponse = await campfireQuery.run({
        url: `${apiUrl}/vm/dashboard/management/tasks/${target}/table`,
        params: {
          filters: {
            programIds: programIds || [],
            activityIds: activityIds || [],
            roleIds: roleIds || [],
            taskIds: selectedTaskIds.length > 0 ? selectedTaskIds : undefined,
          },
          range: range || undefined,
          page: 1,
          limit: 100,
        },
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'brackets', encode: false });
        },
      });

      if (!firstPageResponse?.data?.data) {
        console.error('No data received from API');
        setDownloadingCsv(false);
        return;
      }

      const { total: totalCount, data: firstPageData } = firstPageResponse.data.data;
      const totalPages = Math.ceil(totalCount / 100);

      // Map first page data
      const allData: TaskTableData[] = firstPageData.map((item: any) => ({
        completedTaskId: item.completed_task_id,
        taskId: item.task_id,
        profileId: item.profile_id,
        userId: item.user_id,
        firstName: item.first_name,
        lastName: item.last_name,
        preferredName: item.preferred_name,
        avatarUrl: item.avatar_object_url,
        email: item.email,
        expiryDate: item.expiry_date,
        taskTitle: item.task_title,
        dateSubmitted: item.date_submitted,
        hasExpired: item.has_expired,
        hasPreviousVersion: item.has_previous_version,
      }));

      // If there are more pages, fetch them in parallel
      if (totalPages > 1) {
        const pagePromises = [];
        for (let pageNum = 2; pageNum <= totalPages; pageNum++) {
          pagePromises.push(
            campfireQuery.run({
              url: `${apiUrl}/vm/dashboard/management/tasks/${target}/table`,
              params: {
                filters: {
                  programIds: programIds || [],
                  activityIds: activityIds || [],
                  roleIds: roleIds || [],
                  taskIds: selectedTaskIds.length > 0 ? selectedTaskIds : undefined,
                },
                range: range || undefined,
                page: pageNum,
                limit: 100,
              },
              paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'brackets', encode: false });
              },
            })
          );
        }

        const responses = await Promise.all(pagePromises);
        responses.forEach((response) => {
          if (response?.data?.data?.data) {
            const mappedData = response.data.data.data.map((item: any) => ({
              completedTaskId: item.completed_task_id,
              taskId: item.task_id,
              profileId: item.profile_id,
              userId: item.user_id,
              firstName: item.first_name,
              lastName: item.last_name,
              preferredName: item.preferred_name,
              avatarUrl: item.avatar_object_url,
              email: item.email,
              expiryDate: item.expiry_date,
              taskTitle: item.task_title,
              dateSubmitted: item.date_submitted,
              hasExpired: item.has_expired === 1 || item.has_expired === '1' || item.has_expired === true,
              hasPreviousVersion:
                item.has_previous_version === 1 ||
                item.has_previous_version === '1' ||
                item.has_previous_version === true,
            }));
            allData.push(...mappedData);
          }
        });
      }

      // Build CSV data from all fetched data
      const csvRows: string[] = [];

      // Create headers based on target type
      const headers: string[] = ['Volunteer Name', 'Email'];

      if (target === 'awaiting') {
        headers.push('Task', 'Date Submitted');
      } else if (target === 'completed') {
        headers.push('Task', 'Date Submitted');
      } else if (target === 'incomplete') {
        headers.push('Task', 'Expired', 'Previous Version');
      } else if (target === 'expiring') {
        headers.push('Task', 'Expiry Date');
      } else {
        headers.push('Task ID');
      }

      // Add header row
      csvRows.push(headers.map((h) => `"${h}"`).join(','));

      // Add data rows
      allData.forEach((row) => {
        const rowData: string[] = [`${row.preferredName} ${row.lastName}`, row.email || ''];

        if (target === 'awaiting') {
          rowData.push(row.taskTitle || '');
          rowData.push(
            row.dateSubmitted
              ? new Date(row.dateSubmitted).toLocaleDateString('en-AU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : ''
          );
        } else if (target === 'completed') {
          rowData.push(row.taskTitle || '');
          rowData.push(
            row.dateSubmitted
              ? new Date(row.dateSubmitted).toLocaleDateString('en-AU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : ''
          );
        } else if (target === 'incomplete') {
          rowData.push(row.taskTitle || '');
          // Values are already converted to boolean during mapping
          rowData.push(row.hasExpired ? 'Yes' : 'No');
          rowData.push(row.hasPreviousVersion ? 'Yes' : 'No');
        } else if (target === 'expiring') {
          rowData.push(row.taskTitle || '');
          rowData.push(
            row.expiryDate
              ? new Date(row.expiryDate).toLocaleDateString('en-AU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : ''
          );
        } else {
          rowData.push(row.taskId);
        }

        csvRows.push(
          rowData.map((val) => `"${escapeDangerousCSVCharacters(replaceDoubleQuoteInString(val))}"`).join(',')
        );
      });

      const csvContent = csvRows.join('\r\n');
      const filename = `${getDefaultTitle().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setDownloadingCsv(false);
    }
  };

  return (
    <>
      <WidgetContainer>
        <WidgetHeader title={title || getDefaultTitle()} subTitle={subTitle || getDefaultSubTitle()}>
          <Box display='flex' alignItems='center'>
            {/* Task Dropdown */}
            <TooltipMenu
              open={openTasksDropdown}
              onClose={() => setOpenTasksDropdown(false)}
              title={
                <Box style={{ padding: '5px 10px', minWidth: '250px', maxHeight: '300px', overflowY: 'auto' }}>
                  {availableTasks.length > 0 ? (
                    <Box>
                      {availableTasks.map((task) => (
                        <FormControlLabel
                          key={task.taskId}
                          control={
                            <Checkbox
                              checked={selectedTaskIds.includes(task.taskId)}
                              onChange={(e) => handleTaskSelection(task.taskId, e.target.checked)}
                              style={{ color: '#fff' }}
                              size='small'
                            />
                          }
                          label={
                            <Box>
                              <Box style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1' }}>
                                {task.cakeTitle}
                              </Box>
                              <Box style={{ fontSize: '14px', color: '#fff', lineHeight: '1.2', marginBottom: '8px' }}>
                                {task.taskTitle}
                              </Box>
                            </Box>
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            margin: '4px 0',
                            color: '#fff',
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box style={{ color: 'rgba(255, 255, 255, 0.7)' }}>No tasks available</Box>
                  )}
                </Box>
              }
            >
              <TabletButton
                size='small'
                onClick={() => setOpenTasksDropdown(true)}
                endIcon={<ExpandMore />}
                style={{
                  height: '32px',
                  border: `1px solid ${theme.color.grey.neutral200}`,
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: theme.color.grey.neutral300,
                  textTransform: 'none',
                  fontWeight: 'normal',
                  padding: '4px 12px',
                  marginRight: '8px',
                }}
              >
                {getTaskDropdownText()}
              </TabletButton>
            </TooltipMenu>

            {/* Expand Button */}
            <IconButton
              size='small'
              onClick={() => setExpandedView(true)}
              style={{
                padding: '4px',
                border: `1px solid ${theme.color.grey.neutral200}`,
                borderRadius: '4px',
                backgroundColor: 'white',
                marginRight: '8px',
              }}
              title='Expand View'
            >
              <Fullscreen style={{ fontSize: '20px', color: theme.color.grey.neutral300 }} />
            </IconButton>

            {/* Download Button */}
            <IconButton
              size='small'
              onClick={handleExportCsv}
              disabled={downloadingCsv}
              style={{
                padding: '4px',
                border: `1px solid ${theme.color.grey.neutral200}`,
                borderRadius: '4px',
                backgroundColor: 'white',
              }}
              title='Download CSV'
            >
              {downloadingCsv ? (
                <CircularProgress size={20} style={{ color: theme.color.grey.neutral300 }} />
              ) : (
                <GetApp style={{ fontSize: '20px', color: theme.color.grey.neutral300 }} />
              )}
            </IconButton>
          </Box>
        </WidgetHeader>
        <WidgetContent>
          <Box width='100%' height='450px'>
            <DataGrid
              columns={columns}
              rows={rows}
              loading={loading}
              pagination
              paginationMode='server'
              rowCount={total}
              page={page}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              components={{
                NoRowsOverlay: CustomNoRowsOverlay,
              }}
            />
          </Box>
        </WidgetContent>
      </WidgetContainer>
      {selectedCompletedTaskId && (
        <VolunteerSubmitValues
          completedTaskId={selectedCompletedTaskId}
          onClose={() => setSelectedCompletedTaskId(null)}
          onUpdate={() => {
            setSelectedCompletedTaskId(null);
            setRefreshKey((prev) => prev + 1); // Refresh the table data
          }}
        />
      )}
      {selectedCompletedTaskData && (
        <VolunteerTaskViewDialog
          open
          completedTaskId={selectedCompletedTaskData.completedTaskId}
          taskId={selectedCompletedTaskData.taskId}
          userId={selectedCompletedTaskData.userId}
          profileId={selectedCompletedTaskData.profileId}
          status='approved'
          onClose={() => {
            setSelectedCompletedTaskData(null);
            setRefreshKey((prev) => prev + 1); // Refresh the table data
          }}
        />
      )}
      {selectedIncompleteTaskData && (
        <VolunteerTaskAddDialog
          open
          taskId={selectedIncompleteTaskData.taskId}
          userId={selectedIncompleteTaskData.userId}
          profileId={selectedIncompleteTaskData.profileId}
          onClose={() => {
            setSelectedIncompleteTaskData(null);
            setRefreshKey((prev) => prev + 1); // Refresh the table data
          }}
        />
      )}

      {/* Expanded View Dialog */}
      <Dialog
        open={expandedView}
        onClose={() => setExpandedView(false)}
        maxWidth={false}
        PaperProps={{
          style: {
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle style={{ flexShrink: 0 }}>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box>
              <Box style={{ fontSize: '20px', fontWeight: 500 }}>{title || getDefaultTitle()}</Box>
              <Box style={{ fontSize: '14px', color: theme.color.grey.neutral300 }}>
                {subTitle || getDefaultSubTitle()}
              </Box>
            </Box>
            <MuiIconButton onClick={() => setExpandedView(false)} size='small'>
              <Close />
            </MuiIconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ flex: 1, overflow: 'hidden', padding: '16px' }}>
          <Box width='100%' height='100%'>
            <DataGrid
              columns={columns}
              rows={rows}
              loading={loading}
              pagination
              paginationMode='server'
              rowCount={total}
              page={page}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              disableColumnMenu
              components={{
                NoRowsOverlay: CustomNoRowsOverlay,
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Contact Menu */}
      {Object.entries(contactMenuAnchor).map(([rowId, anchorEl]) => {
        if (!anchorEl) return null;
        const rowData = data.find((item) => `${item.taskId}-${item.profileId}` === rowId);
        if (!rowData) return null;

        return (
          <Popover
            key={rowId}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setContactMenuAnchor((prev) => ({ ...prev, [rowId]: null }))}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              style: {
                backgroundColor: theme.color.grey.neutralBrand800,
                borderRadius: '4px',
              },
            }}
          >
            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
            >
              <Button
                className={classes.contactButton}
                startIcon={<Email />}
                onClick={() => {
                  setContactMenuAnchor((prev) => ({ ...prev, [rowId]: null }));
                  setEmailDialogOpen((prev) => ({ ...prev, [rowId]: true }));
                }}
              >
                Email
              </Button>
              {orgInfo?.smsEnabled && (
                <Button
                  className={classes.contactButton}
                  startIcon={<ChatBubble />}
                  onClick={() => {
                    setContactMenuAnchor((prev) => ({ ...prev, [rowId]: null }));
                    setSmsDialogOpen((prev) => ({ ...prev, [rowId]: true }));
                  }}
                >
                  SMS
                </Button>
              )}
            </Box>
          </Popover>
        );
      })}

      {/* Email Dialogs */}
      {Object.entries(emailDialogOpen).map(([rowId, isOpen]) => {
        if (!isOpen) return null;
        const rowData = data.find((item) => `${item.taskId}-${item.profileId}` === rowId);
        if (!rowData) return null;

        const volunteerData = {
          volunteerId: rowData.userId,
          profile: {
            profileId: rowData.profileId,
            firstName: rowData.firstName,
            lastName: rowData.lastName,
            preferredName: rowData.preferredName,
            avatarUrl: rowData.avatarUrl,
            email: '',
          },
          programs: [],
          dateDeactivated: null,
          rosterStatus: 'active' as const,
        } as any;

        return (
          <VolunteerCommonProfileEmailDialog
            key={rowId}
            volunteers={volunteerData}
            open
            onClose={() => setEmailDialogOpen((prev) => ({ ...prev, [rowId]: false }))}
          />
        );
      })}

      {/* SMS Dialogs */}
      {Object.entries(smsDialogOpen).map(([rowId, isOpen]) => {
        if (!isOpen) return null;
        const rowData = data.find((item) => `${item.taskId}-${item.profileId}` === rowId);
        if (!rowData) return null;

        const volunteerData = {
          volunteerId: rowData.userId,
          profile: {
            profileId: rowData.profileId,
            firstName: rowData.firstName,
            lastName: rowData.lastName,
            preferredName: rowData.preferredName,
            avatarUrl: rowData.avatarUrl,
            email: '',
          },
          programs: [],
          dateDeactivated: null,
          rosterStatus: 'active' as const,
        } as any;

        return (
          <VolunteerCommonProfileSmsDialog
            key={rowId}
            volunteers={volunteerData}
            open
            onClose={() => setSmsDialogOpen((prev) => ({ ...prev, [rowId]: false }))}
          />
        );
      })}
    </>
  );
};
